import React from 'react';
import { NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    ShoppingCart,
    Settings,
    Share2,
    X,
    LogOut,
    History,
    Star,
    Send,
    Bot,
    Activity,
    LineChart,
    Banknote,
    Link as LinkIcon,
    Shield,
    Globe,
    PanelLeftClose,
    PanelLeftOpen,
    Gift,
} from 'lucide-react';
import { API_BASE_URL } from '../../config';
const GRAFANA_URL = typeof window !== 'undefined' ? `${window.location.origin}/grafana/` : '';

const menuItems = [
    { path: '/', label: 'Дашборд', icon: LayoutDashboard, section: 'dashboard' },
    { path: '/orders', label: 'Заказы CRM', icon: ShoppingCart, section: 'orders' },
    { path: '/referrals', label: 'Реклама', icon: Share2, section: 'referrals' },
    { path: '/partner-bots', label: 'Партнёрские боты', icon: Bot, section: 'partner_bots' },
    { path: '/withdrawals', label: 'Заявки на вывод', icon: Banknote, section: 'withdrawals' },
    { path: '/digital-goods', label: 'Цифровые товары', icon: Star, section: 'digital_goods' },
    { path: '/reviews', label: 'Отзывы', icon: Star, section: 'reviews' },
    { path: '/broadcasts', label: 'Рассылки', icon: Send, section: 'broadcasts' },
    { path: '/logs', label: 'Логи системы', icon: History, section: 'logs' },
    { path: '/prices', label: 'Цены', icon: Settings, section: 'prices' },
    { path: '/site-content', label: 'Сайт', icon: Globe, section: 'site_content' },
    { path: '/bot-settings', label: 'Настройки бота', icon: Settings, section: 'bot_settings' },
    { path: '/loyalty', label: 'Бонусная программа', icon: Gift, section: 'bot_settings' },
    { path: '/frog', label: 'Получить реквизиты', icon: LinkIcon, section: 'frog' },
    { path: '/analytics', label: 'Аналитика', icon: LineChart, section: 'analytics' },
    { path: '/anti-fraud', label: 'Антифрод', icon: Shield, section: 'anti_fraud' },
    { path: '/clients', label: 'Клиенты', icon: ShoppingCart, section: 'clients' },
    { path: '/admin-users', label: 'Пользователи админки', icon: Settings, section: 'admin_users' },
    { path: '/roles', label: 'Роли и доступы', icon: Shield, section: 'roles' },
    { label: 'Графики', icon: LineChart, external: true, href: GRAFANA_URL, section: 'grafana' },
    { path: '/metrics', label: 'Метрики', icon: Activity, section: 'metrics' },
];

const Sidebar = ({ onLogout, isMobileMenuOpen, setIsMobileMenuOpen }) => {
    const [role, setRole] = React.useState('admin');
    const [permissions, setPermissions] = React.useState({});
    const [isCollapsed, setIsCollapsed] = React.useState(() => {
        try {
            return window.localStorage.getItem('admin-sidebar-collapsed') === '1';
        } catch (_) {
            return false;
        }
    });

    const toggleCollapsed = () => {
        setIsCollapsed((current) => {
            const next = !current;
            try {
                window.localStorage.setItem('admin-sidebar-collapsed', next ? '1' : '0');
            } catch (_) {}
            return next;
        });
    };

    React.useEffect(() => {
        const load = async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/auth/me`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (r.ok) {
                    const me = await r.json();
                    setRole(me.role || 'admin');
                    setPermissions(me.permissions || {});
                }
            } catch (_) {}
        };
        load();
    }, []);

    const canView = (section) => {
        if (!section) return true;
        return !!permissions[section]?.view;
    };

    return (
        <aside className={`fixed inset-y-0 left-0 z-40 h-screen w-64 bg-slate-900 text-white transform transition-[width,transform] duration-200 ease-out md:translate-x-0 ${isCollapsed ? 'md:w-20' : 'md:w-64'} ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:static md:h-full md:block flex flex-col`}>
            <div className="h-full flex flex-col">
                <div className={`min-h-[73px] border-b border-slate-800 flex items-center justify-between px-6 ${isCollapsed ? 'md:justify-center md:px-3' : ''}`}>
                    <h1 className={`text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400 ${isCollapsed ? 'md:hidden' : ''}`}>
                        ЛОГО
                    </h1>
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white"
                        title={isCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
                        aria-label={isCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
                    >
                        {isCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
                    </button>
                    <button onClick={() => setIsMobileMenuOpen(false)} className="md:hidden text-slate-400"><X size={24} /></button>
                </div>

                <nav className={`flex-1 space-y-2 overflow-y-auto px-4 py-4 ${isCollapsed ? 'md:px-3' : ''}`}>
                    {menuItems.filter((item) => canView(item.section)).map((item) =>
                        item.external ? (
                            <a
                                key={item.label}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsMobileMenuOpen(false)}
                                title={isCollapsed ? item.label : undefined}
                                className={`w-full flex items-center gap-3 rounded-lg py-3 transition-colors text-slate-400 hover:bg-slate-800 hover:text-white ${isCollapsed ? 'md:justify-center md:px-0' : 'px-4'}`}
                            >
                                <item.icon size={20} className="shrink-0" /><span className={isCollapsed ? 'md:hidden' : ''}>{item.label}</span>
                            </a>
                        ) : (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                title={isCollapsed ? item.label : undefined}
                                className={({ isActive }) =>
                                    `w-full flex items-center gap-3 rounded-lg py-3 transition-colors ${isCollapsed ? 'md:justify-center md:px-0' : 'px-4'} ${
                                        isActive
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                    }`
                                }
                            >
                                <item.icon size={20} className="shrink-0" /><span className={isCollapsed ? 'md:hidden' : ''}>{item.label}</span>
                            </NavLink>
                        )
                    )}
                </nav>

                <div className={`border-t border-slate-800 bg-slate-900 p-4 ${isCollapsed ? 'md:px-3' : ''}`}>
                    <div className={`flex items-center ${isCollapsed ? 'md:flex-col md:gap-3' : 'justify-between px-2'}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-xs">
                                AD
                            </div>
                            <div className={`hidden md:block ${isCollapsed ? 'md:hidden' : ''}`}>
                                <p className="text-sm font-medium">{role}</p>
                                <p className="text-xs text-slate-500">Online</p>
                            </div>
                        </div>
                        <button
                            onClick={onLogout}
                            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors"
                            title="Выйти"
                        >
                            <LogOut size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
